import { FC, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { Icon } from "../../../components/Icon/Icon";
import { colors } from "../../../styles/colors";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import {
  useDeleteBulkNotification,
  useRelaunchBulkNotification,
} from "../../../api/notification";

interface Props {
  anchorEl: null | HTMLElement;
  open: boolean;
  handleClose: () => void;
  /** The rows currently ticked in the table. */
  selected: TNotification[];
  fleetId: number;
  /** Lets the page clear its selection once an action has run. */
  onDone?: () => void;
}

/**
 * Bulk actions for the notifications table.
 *
 * A distinct component from CustomMenuEnchorNotification, which is the
 * per-row menu. That one takes a single `notification` and disables both of
 * its items when it is absent — so reusing it here, where there is no single
 * notification, left every action permanently greyed out no matter how many
 * rows were ticked. The two menus look alike and mean different things.
 */
export const NotificationBulkActions: FC<Props> = ({
  anchorEl,
  open,
  handleClose,
  selected,
  fleetId,
  onDone,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { mutate: relaunchBulk, isPending: isRelaunching } =
    useRelaunchBulkNotification();
  const { mutate: deleteBulk, isPending: isDeleting } =
    useDeleteBulkNotification(fleetId);

  /*
   * Virtual notifications (e.g. "upcoming_event_123") are computed server-side
   * and never persisted, so they have no numeric id and cannot be relaunched
   * or deleted. Filtering here means the count on the buttons matches what
   * will actually happen, rather than promising an action on rows the API
   * will silently drop.
   */
  const actionable = selected.filter((n) => Number.isFinite(Number(n?.id)));
  const ids = actionable.map((n) => Number(n.id));
  const count = ids.length;
  const busy = isRelaunching || isDeleting;

  const runRelaunch = () => {
    if (!count) return;
    relaunchBulk(ids, { onSuccess: () => onDone?.() });
    handleClose();
  };

  const runDelete = () => {
    deleteBulk(ids, { onSuccess: () => onDone?.() });
    setConfirmOpen(false);
    onDone?.();
  };

  return (
    <>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-label": t("common.groupeActions", "Actions groupées") }}
      >
        {/* Says why it is unavailable instead of showing two dead rows. The
            previous menu was permanently disabled with no explanation at all. */}
        {count === 0 && (
          <MenuItem disabled>
            <Typography fontSize={13}>
              {t("notification.selectFirst", "Sélectionnez au moins une ligne")}
            </Typography>
          </MenuItem>
        )}

        {count > 0 && (
          <MenuItem onClick={runRelaunch} disabled={busy}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isRelaunching ? (
                <CircularProgress size={12} />
              ) : (
                <Icon name="bell2" width={"12px"} height={"12px"} color={colors.BLACK} />
              )}
              <CustomTypography
                text={t("notification.relaunchSelected", {
                  defaultValue: "Relancer ({{count}})",
                  count,
                })}
                variant={"link"}
              />
            </span>
          </MenuItem>
        )}

        {count > 0 && (
          <MenuItem onClick={() => setConfirmOpen(true)} disabled={busy}>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="trash" width={"12px"} height={"12px"} color={colors.BLACK} />
              <CustomTypography
                text={t("notification.deleteSelected", {
                  defaultValue: "Supprimer ({{count}})",
                  count,
                })}
                variant={"link"}
              />
            </span>
          </MenuItem>
        )}
      </Menu>

      {/*
        Deletion is confirmed because it is permanent: the API force-deletes,
        so there is no trash to recover from and no undo to offer.
      */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {t("notification.deleteConfirmTitle", "Supprimer ces notifications ?")}
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14}>
            {t("notification.deleteConfirmBody", {
              defaultValue:
                "{{count}} notification(s) seront définitivement supprimées. Cette action est irréversible.",
              count,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmOpen(false)}>
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={runDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {t("common.delete", "Supprimer")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationBulkActions;
